import { Moment } from 'moment';
import { Ncm } from '../../operational/Ncm/Ncm.types';

import {
  SupplierClassification,
  SupplierType,
} from '../../people/Suppliers/Suppliers.types';
import {
  PurchaseDeliveryType,
  PurchaseOperationDestination,
  PurchaseTaxRegime,
} from '../Purchase.types';
import { Status } from '@/utils/enum/statusEnum';

export enum PurchaseImportItemType {
  part = 'PART',
  consumable = 'CONSUMABLE',
}

export interface PurchaseListResponse {
  code: number;
  id: number;
  company: string;
  supplier: string;
  documentNumber: string;
  releaseDate: string;
  situation: Status;
}

export interface PurchaseImportResponse {
  accountPayableGrouping: number;
  active: boolean;
  additionalData: string;
  applyDeliveryExpensesBcIcmsSt: boolean;
  applyDeliveryExpensesBcIpi: boolean;
  changeRegisteredData: boolean;
  importedPurchasePartslink24: boolean;
  code: number;
  companies: CompanyPurchaseImport[];
  individuallyExpenses: boolean;
  deductIcmsUnencumbered: boolean;
  deliveryNote: boolean;
  deliveryType: PurchaseDeliveryType;
  deliveryValue: number;
  departureDate: string;
  discountValue: number;
  documentNumber: string;
  documentTypeId: number;
  expensesValue: number;
  groupedIn: number;
  id: number;
  importedPurchase: boolean;
  issueDate: string;
  keyNfe: string;
  modelNf: string;
  operationDestination: PurchaseOperationDestination;
  purchaseItems: PurchaseItems[];
  releaseDate: string;
  seriesNf: string;
  situation: Status;
  supplierDTO: PurchaseImportSupplier;
  totalAmount: number;
  valueCofins: number;
  valueCofinsSt: number;
  valueFcpStNfc: number;
  valueIcms: number;
  valueIcmsSt: number;
  valueIpi: number;
  valueIssqn: number;
  valueNormalFcp: number;
  valuePis: number;
  valuePisSt: number;
  valuevBC: number;
}

export interface CompanyPurchaseImport {
  id: number;
  cnpj: string;
  companyAddress: {
    id: number;
    zip: string;
    street: string;
    number: string;
    district: string;
    cityId: number;
  };
  name: string;
}

export interface PurchaseItems {
  manualLink: boolean;
  duplicatePartBarcode: boolean;
  duplicateConsumableBarcode: boolean;
  barCode: string;
  companyCode: number;
  amountReturned?: number;
  cfopId?: number;
  entryCode?: string;
  cfop: {
    billing: boolean;
    code: string;
    description: string;
    id: number;
    inState: boolean;
    output: boolean;
    outsideCountry: boolean;
  };
  complementId?: number;
  consumableDTO?: PurchaseConsumableDTO;
  consumableId?: number;
  discount?: number;
  id: number;
  manufacturerId?: number;
  name: string;
  ncmId?: number;
  ncm: Ncm;
  partDTO?: PurchasePartDTO;
  partId?: number;
  productCode: string;
  internalCode: string;
  purchaseItemTaxes: PurchaseItemTaxes;
  quantity: number;
  subGroupId?: number;
  totalAmount?: number;
  type: PurchaseImportItemType;
  unitMeasurement?: string;
  unitaryValue: number;
  workOrderItemId?: number;
  workOrderNumber?: string;
  itemIndex?: number;
  linked: boolean;
  isAlter: boolean;
  checked: boolean;
  index: number;
  key: string;
  cloneKey?: string;
  workOrderPurchaseQuotations?: {
    id: number;
    quantitySentPurchase: number;
    quantitypurchased: number;
    requestedQuantity: number;
  };
}

export interface PurchaseConsumableDTO {
  active: boolean;
  barCode: string;
  cfopId: number;
  comment: string;
  id: number;
  imageUrl: string;
  lifespan: number;
  manufacturerId: number;
  manufacturingCode: string;
  name: string;
  ncmId: number;
  sku: string;
  subGroupId: number;
  unit: string;
  unitAlternative: string;
  unitConversionFactor: number;
}

export interface PurchasePartDTO {
  active: boolean;
  barCode: string;
  cfopId: number;
  comment: string;
  id: number;
  imageUrl: string;
  includeInDiagnosis: boolean;
  kmToMaintenance: number;
  lifespan: number;
  localizaCode: string;
  manufacturerId: number;
  manufacturingCode: string;
  companyCode: number;
  name: string;
  ncmId: number;
  sku: string;
  subGroupId: number;
  unit: string;
  unitAlternative: string;
  unitConversionFactor: number;
}

export interface PurchaseItemTaxes {
  additionalValueMarginSt?: number;
  baseCofins?: number;
  baseCofinsSt?: number;
  baseCsll?: number;
  baseFcp?: number;
  baseFcpSt?: number;
  baseIcms?: number;
  baseIcmsSt?: number;
  baseIcmsStWithheld?: number;
  baseInterstateIcms?: number;
  baseIpi?: number;
  baseIssqn?: number;
  basePis?: number;
  basePisSt?: number;
  calcPercentCofins?: boolean;
  calcPercentCofinsSt?: boolean;
  calcPercentPis?: boolean;
  calcPercentPisSt?: boolean;
  csosnIcms?: string;
  cstCofins?: string;
  cstIcms?: string;
  cstIpi?: string;
  cstPis?: string;
  deliveryValue?: number;
  expenseValue?: number;
  id?: number;
  insuranceValue?: number;
  modBcIcms?: number;
  modBcIcmsSt?: number;
  motDesIcms?: number;
  originIcms?: number;
  percentFcp?: number;
  percentProvisionalParticipation?: number;
  quotaBcOp?: number;
  quotaCofins?: number;
  quotaCofinsSt?: number;
  quotaCsll?: number;
  quotaDif?: number;
  quotaFcp?: number;
  quotaFcpSt?: number;
  quotaIcms?: number;
  quotaIcmsSt?: number;
  quotaIcmsUfDestination?: number;
  quotaInterstateIcms?: number;
  quotaIpi?: number;
  quotaIrpj?: number;
  quotaIssqn?: number;
  quotaMvaSt?: number;
  quotaPis?: number;
  quotaPisSt?: number;
  reductionIcms?: number;
  reductionIcmsSt?: number;
  taxRegime?: PurchaseTaxRegime;
  ufSt?: string;
  valueCofins?: number;
  valueCofinsSt?: number;
  valueCsll?: number;
  valueFcp?: number;
  valueFcpSt?: number;
  valueIcms?: number;
  valueIcmsDif?: number;
  valueIcmsFcp?: number;
  valueIcmsOp?: number;
  valueIcmsSt?: number;
  valueIcmsStWithheld?: number;
  valueIcmsUnencumbered?: number;
  valueInterstateDestinationIcms?: number;
  valueInterstateOriginIcms?: number;
  valueIpi?: number;
  valueIrpj?: number;
  valueIssqn?: number;
  valuePis?: number;
  valuePisSt?: number;
  valueTotal?: number;
  valueUnityCofins?: number;
  valueUnityCofinsSt?: number;
  valueUnityPis?: number;
  valueUnityPisSt?: number;
}

export interface PurchaseImportSupplier {
  address: PurchaseAddress;
  classification: SupplierClassification;
  cnpj: string;
  cpf: string;
  id: number;
  matrixId: number;
  municipalRegistration: string;
  name: string;
  nickName: string;
  note: string;
  stateRegistration: string;
  type: SupplierType;
}

export interface PurchaseAddress {
  cityId: number;
  city: string;
  state: string;
  complement: string;
  district: string;
  id: number;
  number: string;
  street: string;
  supplierId: number;
  zipCode: string;
}

export interface PurchaseImportXmlRequest {
  file: File;
}

export interface PurchaseImportFormValues {
  recipient: string;
  cnpjRecipient: string;
  issuer: string;
  cnpjIssuer: string;
  street: string;
  number: string;
  complement: string;
  city: string;
  state: string;
  district: string;
}

export interface ConfirmPurchaseFormValues {
  keyNfe: string;
  supplierName: string;
  documentTypeId: number;
  documentNumber: string;
  modelNf: string;
  seriesNf: string;
  issueDate: Moment;
  departureDate: Moment;
  totalAmount: number;
  items: PurchaseItems[];
}

export interface ConfirmPurchaseModalFormValues {
  itemsCfop: PurchaseItems[];
  itemsNcm: PurchaseItems[];
}

interface PurchaseImportCofirmItems {
  amountReturned?: number;
  cfopId?: number;
  complementId?: number;
  consumableId?: number;
  discount?: number;
  id?: number;
  manufacturerId?: number;
  name?: string;
  ncmId?: number;
  partId?: number;
  productCode?: string;
  purchaseId?: number;
  purchaseItemTaxes?: PurchaseItemTaxes;
  quantity?: number;
  subGroupId?: number;
  totalAmount?: number;
  type?: PurchaseImportItemType;
  unitMeasurement?: string;
  unitaryValue?: number;
  workOrderItemId?: number;
  workOrderPurchaseQuotations?: {
    id?: number;
    quantitySentPurchase?: number;
    quantitypurchased?: number;
    requestedQuantity?: number;
    workOrderItemId?: number;
  };
}

export interface PurchaseCancelRequest {
  purchaseId: number;
  cancellationReason: string;
}

export interface PurchaseImportConfirmRequest {
  accountPayableGrouping: number;
  active: boolean;
  additionalData: string;
  applyDeliveryExpensesBcIcmsSt: boolean;
  applyDeliveryExpensesBcIpi: boolean;
  changeRegisteredData: boolean;
  code: number;
  companyId: number;
  deductIcmsUnencumbered: boolean;
  deliveryNote: boolean;
  deliveryType: PurchaseDeliveryType;
  deliveryValue: number;
  departureDate: string;
  discountValue: number;
  documentNumber: string;
  documentTypeId: number;
  expensesValue: number;
  groupedIn: number;
  id: number;
  importedPurchasePartslink24: boolean;
  individuallyExpenses: boolean;
  issueDate: string;
  keyNfe: string;
  modelNf: string;
  operationDestination: PurchaseOperationDestination;
  purchaseItems: PurchaseImportCofirmItems[];
  releaseDate: string;
  seriesNf: string;
  situation: Status;
  supplierDTO: PurchaseImportSupplier;
  totalAmount: number;
  valueCofins: number;
  valueCofinsSt: number;
  valueFcpStNfc: number;
  valueIcms: number;
  valueIcmsSt: number;
  valueIpi: number;
  valueIssqn: number;
  valueNormalFcp: number;
  valuePis: number;
  valuePisSt: number;
  valuevBC: number;
}

export interface PurchaseImportConfirmResponse {
  purchaseId: number;
}
